import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  billingApi,
  PLAN_RANK,
  PlanDefinition,
  PlanKey,
} from '../../api/billing';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import { getApiErrorMessage } from '../../utils/errorHandling';

const daysUntil = (iso: string | null): number | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
};

const STATUS_LABEL: Record<string, { label: string; classes: string }> = {
  active: { label: 'Active', classes: 'bg-emerald-100 text-emerald-700' },
  trialing: { label: 'Trial', classes: 'bg-sky-100 text-sky-700' },
  past_due: { label: 'Past due', classes: 'bg-amber-100 text-amber-700' },
  canceled: { label: 'Canceled', classes: 'bg-rose-100 text-rose-700' },
  incomplete: { label: 'Incomplete', classes: 'bg-slate-200 text-slate-700' },
};

const formatDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

export default function BillingSettings() {
  const { status, refresh } = useEntitlements();
  const [plans, setPlans] = useState<PlanDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyPlan, setBusyPlan] = useState<PlanKey | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [renewLoading, setRenewLoading] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [manageHint, setManageHint] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([billingApi.getPlans(), refresh()])
      .then(([p]) => {
        if (active) setPlans(p);
      })
      .catch(() => undefined)
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusMeta = status ? STATUS_LABEL[status.status] ?? STATUS_LABEL.incomplete : null;
  const trialEnds = formatDate(status?.trialEndsAt ?? null);
  const periodEnds = formatDate(status?.currentPeriodEnd ?? null);
  const trialDaysLeft =
    status?.status === 'trialing' ? daysUntil(status.trialEndsAt) : null;
  const hasPaidSubscription =
    status?.hasPaidSubscription ?? status?.hasStripeSubscription ?? false;
  const commercial = Boolean(status?.billingConfigured) && !status?.billingExempt;

  const applyStatus = async (next: Awaited<ReturnType<typeof billingApi.changePlan>>) => {
    if ('checkout' in next && next.checkout && next.url) {
      window.location.href = next.url;
      return;
    }
    if ('plan' in next) {
      await refresh();
      toast.success(`Plan updated to ${next.planName}`);
    }
  };

  const handleChoose = async (plan: PlanDefinition) => {
    if (!commercial) return;
    if (hasPaidSubscription && status && PLAN_RANK[plan.key] < PLAN_RANK[status.plan]) {
      const ok = window.confirm(
        `Downgrade to ${plan.name} takes effect immediately. Usage limits of the lower plan apply now. Continue?`,
      );
      if (!ok) return;
    }
    setBusyPlan(plan.key);
    try {
      // Prefer in-place change when subscribed; otherwise Checkout / payment link.
      if (hasPaidSubscription) {
        const result = await billingApi.changePlan(plan.key);
        await applyStatus(result);
        return;
      }
      if (plan.paymentLink && !hasPaidSubscription) {
        window.location.href = plan.paymentLink;
        return;
      }
      const result = await billingApi.changePlan(plan.key);
      await applyStatus(result);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not change plan'));
    } finally {
      setBusyPlan(null);
    }
  };

  const handleManage = async () => {
    setPortalLoading(true);
    setManageHint(null);
    try {
      const result = await billingApi.createPortal();
      if (result.checkout) {
        toast.success('Opening Paystack to update your payment method');
        window.location.href = result.url;
        return;
      }
      const opened = window.open(result.url, '_blank', 'noopener,noreferrer');
      if (!opened) {
        window.location.href = result.url;
        return;
      }
      toast.success('Opened subscription management in a new tab');
      await refresh();
    } catch (err) {
      if (status?.manageUrl) {
        const opened = window.open(status.manageUrl, '_blank', 'noopener,noreferrer');
        if (!opened) window.location.href = status.manageUrl;
        return;
      }
      const message = getApiErrorMessage(
        err,
        'Could not open payment management. Choose a plan below to check out, or contact support.',
      );
      setManageHint(message);
      toast.error(message);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCancel = async (immediate: boolean) => {
    setCancelLoading(true);
    try {
      const next = await billingApi.cancelSubscription(immediate);
      await refresh();
      setConfirmCancel(false);
      toast.success(
        immediate
          ? 'Subscription canceled'
          : `Subscription will cancel on ${formatDate(next.currentPeriodEnd) ?? 'period end'}`,
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not cancel subscription'));
    } finally {
      setCancelLoading(false);
    }
  };

  const handleRenew = async () => {
    setRenewLoading(true);
    try {
      const next = await billingApi.renewSubscription();
      if ('checkout' in next && next.checkout && next.url) {
        window.location.href = next.url;
        return;
      }
      await refresh();
      const renewsOn =
        'currentPeriodEnd' in next ? formatDate(next.currentPeriodEnd) : periodEnds;
      toast.success(
        renewsOn
          ? `Auto-renew enabled — next renewal ${renewsOn}`
          : 'Auto-renew enabled — subscription will renew at period end',
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not renew subscription'));
    } finally {
      setRenewLoading(false);
    }
  };

  const planActionLabel = (plan: PlanDefinition): string => {
    if (!status) return `Choose ${plan.name}`;
    if (!hasPaidSubscription || status.status === 'canceled') {
      return `Choose ${plan.name}`;
    }
    const currentRank = PLAN_RANK[status.plan] ?? 0;
    const nextRank = PLAN_RANK[plan.key] ?? 0;
    if (nextRank > currentRank) return `Upgrade to ${plan.name}`;
    if (nextRank < currentRank) return `Switch to ${plan.name}`;
    return `Choose ${plan.name}`;
  };

  const canCancel =
    commercial &&
    Boolean(status?.hasLinkedSubscription ?? status?.hasPaidSubscription) &&
    status?.status !== 'canceled' &&
    !status?.cancelAtPeriodEnd;

  const needsRenew =
    commercial &&
    (Boolean(status?.cancelAtPeriodEnd) ||
      status?.status === 'canceled' ||
      status?.status === 'incomplete');

  const providerLabel =
    status?.paymentProvider === 'paystack'
      ? 'Paystack'
      : status?.paymentProvider === 'stripe'
        ? 'Stripe'
        : null;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg leading-6 font-medium text-slate-900 mb-1">Manage subscription</h3>
        <p className="text-sm text-slate-500">
          View your plan, update payment details, or switch tiers. Paid plans renew automatically
          unless you cancel.
        </p>
      </div>

      {!loading && status?.billingExempt && (
        <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <InformationCircleIcon className="h-5 w-5 shrink-0 text-slate-600" aria-hidden="true" />
          <div className="text-sm text-slate-800">
            <p className="font-medium">This host is not a paid customer instance.</p>
            <p className="mt-0.5 text-slate-600">
              BirdMan Demo is a go-to-market environment. Its infrastructure cost is marketing, not
              a subscription. Customers subscribe during public signup; their instance then carries
              the Starter, Professional, or Business plan.
            </p>
          </div>
        </div>
      )}

      {!loading && status && !status.billingConfigured && (
        <div className="flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50 p-4">
          <InformationCircleIcon className="h-5 w-5 shrink-0 text-sky-600" aria-hidden="true" />
          <div className="text-sm text-sky-800">
            <p className="font-medium">This instance includes the full suite.</p>
            <p className="mt-0.5 text-sky-700">
              Self-serve billing is not connected here. Contact support if you need invoices or a
              plan change.
            </p>
          </div>
        </div>
      )}

      {!loading &&
        commercial &&
        status.hasPaidSubscription &&
        status.hasLinkedSubscription === false && (
          <div className="flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50 p-4">
            <InformationCircleIcon className="h-5 w-5 shrink-0 text-sky-600" aria-hidden="true" />
            <div className="text-sm text-sky-800">
              <p className="font-medium">Payment details are updated through Paystack checkout.</p>
              <p className="mt-0.5 text-sky-700">
                This instance is on a paid plan. Manage payment and plan upgrades open a secure
                Paystack checkout to confirm the card on file.
              </p>
            </div>
          </div>
        )}

      {!loading && commercial && status.status === 'past_due' && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Payment past due.</p>
            <p className="mt-0.5 text-amber-700">
              Your last payment failed. Update your payment method to avoid losing access.
            </p>
            <button
              type="button"
              onClick={handleManage}
              disabled={portalLoading}
              className="mt-2 inline-flex items-center rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-500 disabled:opacity-60"
            >
              {portalLoading ? 'Opening…' : 'Update payment method'}
            </button>
          </div>
        </div>
      )}

      {!loading &&
        commercial &&
        status.cancelAtPeriodEnd &&
        status.status !== 'canceled' && (
          <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-rose-600" aria-hidden="true" />
            <div className="text-sm text-rose-800">
              <p className="font-medium">Cancellation scheduled — will not auto-renew</p>
              <p className="mt-0.5 text-rose-700">
                Access continues until {periodEnds ?? 'the end of the billing period'}. Renew
                anytime before then. If Paystack cannot reactivate the current subscription,
                Checkout opens so you can start a new one on the same plan.
              </p>
              <button
                type="button"
                onClick={handleRenew}
                disabled={renewLoading}
                className="mt-2 inline-flex items-center rounded-md bg-rose-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
              >
                {renewLoading ? 'Renewing…' : 'Renew at period end'}
              </button>
            </div>
          </div>
        )}

      {!loading &&
        commercial &&
        (status.status === 'canceled' || status.status === 'incomplete') && (
          <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <InformationCircleIcon className="h-5 w-5 shrink-0 text-slate-600" aria-hidden="true" />
            <div className="text-sm text-slate-800">
              <p className="font-medium">Subscription not active</p>
              <p className="mt-0.5 text-slate-600">
                Renew to start a new billing period on {status.planName}, or choose another plan below.
              </p>
              <button
                type="button"
                onClick={handleRenew}
                disabled={renewLoading}
                className="mt-2 inline-flex items-center rounded-md bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-500 disabled:opacity-60"
              >
                {renewLoading ? 'Starting…' : `Renew ${status.planName}`}
              </button>
            </div>
          </div>
        )}

      {!loading &&
        commercial &&
        status.status === 'trialing' &&
        trialDaysLeft !== null &&
        trialDaysLeft <= 7 && (
          <div className="flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50 p-4">
            <InformationCircleIcon className="h-5 w-5 shrink-0 text-sky-600" aria-hidden="true" />
            <div className="text-sm text-sky-800">
              <p className="font-medium">
                {trialDaysLeft <= 0
                  ? 'Your trial has ended.'
                  : `Your trial ends in ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'}.`}
              </p>
              <p className="mt-0.5 text-sky-700">
                Choose a plan below to keep full access without interruption.
              </p>
            </div>
          </div>
        )}

      {status && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Current plan</p>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className="text-xl font-semibold text-slate-900">{status.planName}</span>
                {statusMeta && (
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusMeta.classes}`}>
                    {statusMeta.label}
                  </span>
                )}
                {commercial && status.autoRenew && (
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
                    Auto-renews
                  </span>
                )}
                {commercial && status.cancelAtPeriodEnd && status.status !== 'canceled' && (
                  <span className="rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700 ring-1 ring-rose-200">
                    Ends this period
                  </span>
                )}
              </div>
              <div className="mt-1 text-sm text-slate-500">
                {status.status === 'trialing' && trialEnds && <span>Trial ends {trialEnds}</span>}
                {status.status !== 'trialing' && periodEnds && (
                  <span>
                    {status.cancelAtPeriodEnd
                      ? `Access until ${periodEnds} (no renewal)`
                      : status.autoRenew
                        ? `Auto-renews ${periodEnds}`
                        : `Period ends ${periodEnds}`}
                  </span>
                )}
                {providerLabel && commercial && (
                  <span className="ml-2 text-slate-400">· Billed with {providerLabel}</span>
                )}
              </div>
            </div>
            {commercial && (
              <div className="flex flex-wrap items-center gap-2">
                {needsRenew && (
                  <button
                    type="button"
                    onClick={handleRenew}
                    disabled={renewLoading}
                    className="inline-flex items-center rounded-md border border-cyan-300 bg-cyan-50 px-3 py-2 text-sm font-semibold text-cyan-800 hover:bg-cyan-100 disabled:opacity-60"
                  >
                    {renewLoading
                      ? 'Working…'
                      : status.cancelAtPeriodEnd
                        ? 'Renew at period end'
                        : 'Renew subscription'}
                  </button>
                )}
                {canCancel && (
                  <button
                    type="button"
                    onClick={() => setConfirmCancel(true)}
                    className="inline-flex items-center rounded-md border border-rose-300 bg-white px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50"
                  >
                    Cancel subscription
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleManage}
                  disabled={portalLoading || status?.canOpenPortal === false}
                  className="inline-flex items-center rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-60"
                >
                  {portalLoading ? 'Opening…' : 'Manage payment'}
                </button>
              </div>
            )}
          </div>
          {manageHint && (
            <p className="mt-3 text-sm text-amber-800">{manageHint}</p>
          )}

          {confirmCancel && (
            <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50/80 p-4">
              <p className="text-sm font-medium text-rose-900">Cancel this subscription?</p>
              <p className="mt-1 text-sm text-rose-800">
                Recommended: cancel at period end so you keep access until{' '}
                {periodEnds ?? 'your renewal date'}. Immediate cancel ends access now.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={cancelLoading}
                  onClick={() => handleCancel(false)}
                  className="inline-flex items-center rounded-md bg-rose-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                >
                  {cancelLoading ? 'Canceling…' : 'Cancel at period end'}
                </button>
                <button
                  type="button"
                  disabled={cancelLoading}
                  onClick={() => handleCancel(true)}
                  className="inline-flex items-center rounded-md border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-800 hover:bg-rose-100 disabled:opacity-60"
                >
                  Cancel immediately
                </button>
                <button
                  type="button"
                  disabled={cancelLoading}
                  onClick={() => setConfirmCancel(false)}
                  className="inline-flex items-center rounded-md px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-white/80"
                >
                  Keep subscription
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {plans.map((plan) => {
          const isCurrent = status?.plan === plan.key && !status?.cancelAtPeriodEnd;
          return (
            <div
              key={plan.key}
              className={`flex flex-col rounded-xl border p-5 shadow-sm ${
                isCurrent ? 'border-cyan-400 ring-1 ring-cyan-200' : 'border-slate-200'
              } bg-white`}
            >
              <div className="flex items-center justify-between">
                <h4 className="text-base font-semibold text-slate-900">{plan.name}</h4>
                {isCurrent && (
                  <span className="rounded-full bg-cyan-100 px-2 py-0.5 text-xs font-semibold text-cyan-700">
                    Current
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-slate-500">{plan.description}</p>
              <div className="mt-3">
                <span className="text-2xl font-bold text-slate-900">R{plan.priceMonthlyZar}</span>
                <span className="text-sm text-slate-500">/mo</span>
                <span className="ml-2 text-xs text-slate-400">≈ ${plan.priceMonthlyUsd}/mo</span>
              </div>
              <ul className="mt-4 flex-1 space-y-2">
                <li className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" />
                  <span>Full product suite</span>
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" />
                  <span>
                    {plan.limits.birds === null
                      ? 'Unlimited birds'
                      : `Up to ${plan.limits.birds.toLocaleString()} birds`}
                  </span>
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" />
                  <span>
                    {plan.limits.users === null
                      ? 'Unlimited users'
                      : `${plan.limits.users} user${plan.limits.users === 1 ? '' : 's'}`}
                  </span>
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" />
                  <span>
                    {plan.limits.aiInvocationsPerMonth === null
                      ? 'Unlimited AI usage'
                      : `${plan.limits.aiInvocationsPerMonth.toLocaleString()} AI calls / mo`}
                  </span>
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" />
                  <span>
                    {plan.limits.storageGb === null
                      ? 'Unlimited storage'
                      : `${plan.limits.storageGb} GB storage`}
                  </span>
                </li>
                {plan.limits.alwaysOnCompute && (
                  <li className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" />
                    <span>Always-on compute allowed</span>
                  </li>
                )}
              </ul>
              <div className="mt-4 text-xs text-slate-400">
                {plan.limits.knowledgeSources === null
                  ? 'Unlimited knowledge sources'
                  : `${plan.limits.knowledgeSources} knowledge sources`}
              </div>
              {!isCurrent && commercial && (
                <button
                  type="button"
                  onClick={() => handleChoose(plan)}
                  disabled={busyPlan !== null}
                  className="mt-4 inline-flex items-center justify-center rounded-md bg-cyan-600 px-3 py-2 text-sm font-semibold text-white hover:bg-cyan-500 disabled:opacity-60"
                >
                  {busyPlan === plan.key ? 'Working…' : planActionLabel(plan)}
                </button>
              )}
              {isCurrent && hasPaidSubscription && status?.hasLinkedSubscription && (
                <p className="mt-4 text-center text-xs text-slate-400">
                  Upgrades apply immediately with proration
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
